import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Alert, Button, Container, FormField, Select, SpaceBetween } from '@amzn/awsui-components-react';

import { useAppSelector } from '../../app/hooks';
import DrishtiPageHeader from '../../components/drishtiPageHeader';
import { isListDrishtiSitesResponse, listDrishtiSites } from '../../services/drishtiHandler';
import { EventName, EventType, logDrishtiMetricEvent } from '../../services/eventService';
import { updateDrishtiSite } from '../../slice/drishtiInstallConfigSlice';

const SITES_UNAVAILABLE = 'Site list: could not load the sites. Check your connection.';
const NO_SITES = 'No sites are on record.';

const toOption = (site: string) => ({ label: site, value: site });

const DrishtiSite = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const persistedSite = useAppSelector((state) => state.drishtiInstallConfig.site);

    const [site, setSite] = useState<string | null>(persistedSite);
    const [sites, setSites] = useState<readonly string[] | null>(null);
    const [listFailed, setListFailed] = useState(false);
    const [listAttempt, setListAttempt] = useState(0);

    const logEvent = (eventType: EventType, miscEventData: {}) =>
        logDrishtiMetricEvent({ eventName: EventName.DRISHTI_SITES_LISTED, eventType, miscEventData });

    useEffect(() => {
        setSites(null);
        setListFailed(false);
        listDrishtiSites((success, result) => {
            if (success && isListDrishtiSitesResponse(result)) {
                setSites(result.sites);
                setSite((current) => (current && result.sites.includes(current) ? current : null));
                logEvent(EventType.SUCCESS, { siteCount: result.sites.length });
            } else {
                setListFailed(true);
                logEvent(EventType.FAILURE, { error: result });
            }
        });
    }, [listAttempt]);

    const isListing = sites === null && !listFailed;

    const onSiteChange = (value?: string) => {
        if (value) {
            setSite(value);
        }
    };

    const onContinue = () => {
        if (!site) {
            return;
        }
        dispatch(updateDrishtiSite(site));
        navigate('/drishti/station');
    };

    return (
        <Container>
            <DrishtiPageHeader title="Select your site" description="Choose the building you are installing in." />
            <SpaceBetween size="l">
                <FormField label="Site">
                    <Select
                        placeholder="Choose a site"
                        filteringType="auto"
                        filteringAriaLabel="Filter sites"
                        selectedOption={site ? toOption(site) : null}
                        options={(sites ?? []).map(toOption)}
                        statusType={isListing ? 'loading' : 'finished'}
                        loadingText="Loading sites"
                        empty={NO_SITES}
                        disabled={listFailed}
                        onChange={(e) => onSiteChange(e.detail.selectedOption.value)}
                    />
                </FormField>

                {listFailed ? (
                    <Alert
                        type="error"
                        action={<Button onClick={() => setListAttempt((attempt) => attempt + 1)}>Retry</Button>}
                    >
                        {site ? `${SITES_UNAVAILABLE} Continue will use your last site, ${site}.` : SITES_UNAVAILABLE}
                    </Alert>
                ) : null}

                <Button variant="primary" onClick={onContinue} disabled={!site || isListing}>
                    Continue
                </Button>
            </SpaceBetween>
        </Container>
    );
};

export default DrishtiSite;
